import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Trash2, ChevronRight, Minus, Plus, ShoppingBag, Truck, Info, Tag, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import CheckoutDialog from '../components/CheckoutDialog';
import { resolveImage } from '../components/admin/ImageUpload';
import { readWastageFlag } from '../components/FlooringCalculator';
import { vatAmount, computeDelivery } from '../utils/pricing';
import api from '../api/client';
import SEO from '../components/SEO';

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal, clearCart, setCollect } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const vatRate = settings?.vat_rate ?? 20;
  const freeThreshold = Number(settings?.free_delivery_threshold ?? 75);
  const standardFee = Number(settings?.standard_delivery_fee ?? 4.95);
  const navigate = useNavigate();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Promo code state — one active promo per order (server enforces this too).
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // { code, discount_amount, type, value }
  const [promoError, setPromoError] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const isTradeTier = user && (user.user_type === 'builder' || user.user_type === 'retailer');

  const applyPromo = async () => {
    const code = (promoInput || '').trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true); setPromoError('');
    try {
      const r = await api.post('/promos/validate', { code, subtotal });
      if (r.data?.ok) {
        setPromo(r.data);
        toast.success(`Promo ${r.data.code} applied — save £${Number(r.data.discount_amount).toFixed(2)}`);
      } else {
        setPromoError(r.data?.error || 'Invalid promo code.');
        setPromo(null);
      }
    } catch (e) {
      setPromoError(e.response?.data?.detail || 'Could not validate code.');
      setPromo(null);
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setPromo(null); setPromoInput(''); setPromoError(''); };

  const deliveryInfo = computeDelivery(items, { threshold: freeThreshold, flatRate: standardFee });
  const delivery = deliveryInfo.total;
  const discountAmount = promo && !isTradeTier ? Number(promo.discount_amount || 0) : 0;
  const subtotalAfterPromo = Math.max(0, +(subtotal - discountAmount).toFixed(2));
  const vat = vatAmount(subtotalAfterPromo + delivery, vatRate);
  const total = +(subtotalAfterPromo + delivery + vat).toFixed(2);
  // Only show "spend £X more for free delivery" when the fallback pool is
  // active and still under the threshold. Products with their own shipping
  // rules don't count toward the free-delivery threshold.
  const fallbackShortfall = deliveryInfo.fallbackSubtotal > 0 && deliveryInfo.fallbackSubtotal < freeThreshold
    ? freeThreshold - deliveryInfo.fallbackSubtotal
    : 0;

  const handleCheckout = () => {
    if (!user) {
      toast.info('Please sign in to checkout');
      navigate('/login?redirect=/cart');
      return;
    }
    setCheckoutOpen(true);
  };

  const onOrderSuccess = () => {
    setCheckoutOpen(false);
    clearCart();
    clearPromo();
    navigate('/orders');
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <SEO title="Your basket" path="/cart" noindex />
      <nav className="flex items-center gap-2 text-xs text-neutral-600 mb-4">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#12263A] font-semibold">Basket</span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-black text-[#12263A] mb-6">Your basket</h1>

      {items.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-sm p-12 text-center">
          <ShoppingBag className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#12263A] mb-2">Your basket is empty</h2>
          <p className="text-neutral-600 mb-6">Add products to get started with your DIY project.</p>
          <button
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
            data-testid="continue-shopping-empty"
            className="inline-block bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-6 py-3 rounded-sm transition-colors"
          >
            Continue shopping
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="bg-white border border-neutral-200 rounded-sm">
            <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-[#12263A]">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
              <button onClick={clearCart} className="text-xs text-neutral-600 hover:text-[#F46A25]">Clear basket</button>
            </div>
            {items.map(item => (
              <div key={item.line_id || item.id} className="p-4 border-b border-neutral-100 last:border-b-0 flex gap-4">
                <Link to={item.variant_id ? `/product/${item.id}?v=${item.variant_id}` : `/product/${item.id}`} className="w-24 h-24 flex-shrink-0 bg-neutral-50 border border-neutral-200 rounded-sm overflow-hidden">
                  <img src={resolveImage(item.image)} alt={item.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase text-neutral-500 font-semibold">{item.brand}</div>
                  <Link to={item.variant_id ? `/product/${item.id}?v=${item.variant_id}` : `/product/${item.id}`} className="text-sm font-semibold text-[#12263A] hover:text-[#F46A25] line-clamp-2">{item.name}</Link>
                  {item.variant_id && (
                    <div className="mt-0.5 text-[11px] text-[#F46A25] font-semibold" data-testid={`cart-variant-tag-${item.line_id}`}>
                      Pack size — variant selected
                    </div>
                  )}
                  {item.coverage_per_box_sqm > 0 && (
                    <div className="mt-1 text-[11px] text-neutral-600" data-testid={`cart-flooring-${item.id}`}>
                      <b>{item.qty}</b> {item.qty === 1 ? 'box' : 'boxes'} · £{item.price.toFixed(2)}/box · covers <b>{(item.qty * item.coverage_per_box_sqm).toFixed(2)} sqm</b>
                      {readWastageFlag(item.id) && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-[#F46A25] font-semibold" data-testid={`cart-wastage-note-${item.id}`}>
                          <Info className="w-2.5 h-2.5" /> Includes 10% wastage
                        </span>
                      )}
                    </div>
                  )}
                  {item.collect && (
                    <button
                      type="button"
                      onClick={() => setCollect(item.id, false, item.variant_id)}
                      data-testid={`cart-line-collect-badge-${item.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#F46A25] bg-orange-50 border border-[#F46A25]/40 hover:bg-orange-100 px-2 py-0.5 rounded-sm"
                      title="Click to switch back to delivery"
                    >
                      <ShoppingBag className="w-3 h-3" /> Collect in store · ships free
                    </button>
                  )}
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center border border-neutral-300 rounded-sm">
                      <button onClick={() => updateQty(item.id, item.qty - 1, item.variant_id)} aria-label="Decrease" className="px-2 py-1 hover:bg-neutral-100"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="px-3 py-1 text-sm font-semibold min-w-[2.5rem] text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1, item.variant_id)} aria-label="Increase" className="px-2 py-1 hover:bg-neutral-100"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-[#12263A]">£{(item.price * item.qty).toFixed(2)}</span>
                      <button onClick={() => removeItem(item.id, item.variant_id)} aria-label="Remove" className="text-neutral-500 hover:text-[#F46A25]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-neutral-200 rounded-sm p-4 h-fit lg:sticky lg:top-32">
            <h3 className="font-bold text-[#12263A] mb-4">Order summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-neutral-600">Subtotal (ex VAT)</span><span className="font-semibold">£{subtotal.toFixed(2)}</span></div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-green-700" data-testid="cart-promo-discount-row">
                  <span>Promo <span className="font-mono text-xs bg-green-50 px-1.5 py-0.5 rounded-sm">{promo.code}</span></span>
                  <span className="font-semibold">− £{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-neutral-600">Delivery</span><span className="font-semibold">{delivery === 0 ? 'FREE' : `£${delivery.toFixed(2)}`}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">VAT ({vatRate}%)</span><span className="font-semibold">£{vat.toFixed(2)}</span></div>
              {fallbackShortfall > 0 && (
                <div className="text-xs text-neutral-600 bg-neutral-50 p-2 rounded-sm flex items-start gap-2">
                  <Truck className="w-4 h-4 text-[#F46A25] mt-0.5 flex-shrink-0" />
                  <span>Spend £{fallbackShortfall.toFixed(2)} more (ex VAT) for free delivery on eligible items</span>
                </div>
              )}
              {deliveryInfo.overrideDelivery > 0 && (
                <div className="text-[11px] text-neutral-500 -mt-1" data-testid="cart-per-product-shipping-note">
                  Includes £{deliveryInfo.overrideDelivery.toFixed(2)} product-specific shipping
                </div>
              )}
              <div className="pt-3 border-t border-neutral-200 flex justify-between items-baseline">
                <span className="font-bold text-[#12263A]">Total <span className="text-[10px] text-neutral-500 font-normal">(inc VAT)</span></span>
                <span className="font-black text-xl text-[#12263A]">£{total.toFixed(2)}</span>
              </div>
            </div>

            {/* Promo code input */}
            {!isTradeTier && (
              <div className="mt-4 pt-4 border-t border-dashed border-neutral-200" data-testid="cart-promo-block">
                {promo ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-sm px-3 py-2" data-testid="cart-promo-applied">
                    <div className="flex items-center gap-2 text-xs">
                      <Tag className="w-3.5 h-3.5 text-green-700" />
                      <span className="font-bold text-green-800">{promo.code}</span>
                      <span className="text-neutral-600">— {promo.type === 'percent' ? `${promo.value}% off` : `£${Number(promo.value).toFixed(2)} off`}</span>
                    </div>
                    <button
                      onClick={clearPromo}
                      data-testid="cart-promo-clear"
                      className="text-neutral-500 hover:text-red-600"
                      aria-label="Remove promo"
                    ><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-bold text-[#12263A] mb-1.5 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Promo code
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoInput}
                        onChange={e => { setPromoInput(e.target.value); setPromoError(''); }}
                        placeholder="Enter code"
                        data-testid="cart-promo-input"
                        className={`flex-1 border rounded-sm px-3 py-2 text-sm uppercase focus:outline-none ${promoError ? 'border-red-500' : 'border-neutral-300 focus:border-[#F46A25]'}`}
                        onKeyDown={e => e.key === 'Enter' && applyPromo()}
                      />
                      <button
                        onClick={applyPromo}
                        disabled={promoBusy || !promoInput.trim()}
                        data-testid="cart-promo-apply"
                        className="bg-[#12263A] hover:bg-[#F46A25] text-white text-xs font-bold px-3 py-2 rounded-sm disabled:opacity-60 inline-flex items-center gap-1"
                      >
                        {promoBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                    {promoError && (
                      <p className="mt-1.5 text-[11px] text-red-600 font-semibold" data-testid="cart-promo-error">{promoError}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <button onClick={handleCheckout} className="w-full mt-4 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-3 rounded-sm transition-colors">
              {user ? 'Checkout' : 'Sign in to checkout'}
            </button>
            <button
              onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
              data-testid="continue-shopping-btn"
              className="block w-full text-center mt-3 text-sm text-[#12263A] hover:text-[#F46A25]"
            >
              ← Continue shopping
            </button>
          </div>
        </div>
      )}
      <CheckoutDialog open={checkoutOpen} onClose={() => setCheckoutOpen(false)} onSuccess={onOrderSuccess} subtotal={subtotalAfterPromo} promoCode={promo?.code} />
    </div>
  );
};

export default CartPage;
